import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import {
  CreateLineItemDto,
  InvoiceWithRelationsDto,
  ResponseDto,
} from "@mimber/schema"
import { invoicesKeys } from "../invoices.utils"

interface AddInvoiceLineItemOptions extends Omit<
  UseMutationOptions<
    ResponseDto<InvoiceWithRelationsDto>,
    Error,
    CreateLineItemDto
  >,
  "mutationFn"
> {}

export const useAddInvoiceLineItem = (
  invoiceUuid: string,
  options: AddInvoiceLineItemOptions = {}
): UseMutationResult<
  ResponseDto<InvoiceWithRelationsDto>,
  Error,
  CreateLineItemDto
> => {
  guardAppUsage("mimber-organization", "useAddInvoiceLineItem")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: CreateLineItemDto) => {
      const response = await server.post<ResponseDto<InvoiceWithRelationsDto>>(
        `invoices/${invoiceUuid}/line-items`,
        data
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: invoicesKeys.findOne(invoiceUuid),
      })
      queryClient.invalidateQueries({ queryKey: invoicesKeys.findAll() })
      options?.onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
